// @ts-strict

import { Component, EventEmitter, Output, OnInit, ViewChild, Input, OnDestroy, ElementRef, AfterViewInit } from '@angular/core';
import { StatisticsSummaryService } from '@services/statistics/statistics-summary.service';
import { StatsSliderComponent } from '@pages/statistics/shared';
import { countries } from '@app/core/helpers/countries';
import { Subject, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { IChosenSortValue } from '@app/core/models/core-models';
import { FilterMenuOption } from '@pages/statistics/shared/interfaces/statistics';
import { filter, take, takeUntil, tap } from 'rxjs/operators';
import { TableService } from '../../table.service';
import { isEmpty } from 'lodash';
import { SharedStatisticsFunctionsService } from '@pages/statistics/shared/shared-statistics-functions/shared-statistics-functions.service';
import { StatisticsCountryFilterItem, Option } from '@services/statistics/statistics.types';
import { ISelectOption } from '@app/shared/types/inputs';

@Component({
  selector: 'app-column-menu',
  templateUrl: './column-menu.component.html',
  styleUrls: ['./column-menu.component.scss']
})
export class ColumnMenuComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(StatsSliderComponent) range!: StatsSliderComponent;
  @ViewChild('columnMenuRef') columnMenu!: ElementRef<HTMLDivElement>;

  @Input() isDetailsTableLayout?: boolean;
  @Input() menuName?: string;
  @Input() menuTitle?: string;
  @Input() desc?: string;
  @Input() icon?: string;
  @Input() currency?: string;
  @Input() tableTitle?: string;
  @Input() activeSortMethod?: any;
  @Input() filterMenuOptions?: FilterMenuOption[];
  @Input() appliedFiltersBar?: any;
  @Input() columnName: string = '';

  @Output() closeMenu = new EventEmitter<string>();
  @Output() getOpenedColumnMenuHeight = new EventEmitter<number>();

  public checkboxError = false;
  public countryList: StatisticsCountryFilterItem[] = [];
  public Object = Object;
  public selectedOptions: Record<string, Option[]> = {};
  public sliceCountriesTo = 10;
  public enableCleanFilters = false;

  public selectOptionsCampaigns: ISelectOption[] = [];

  private searchValueName = '';
  private searchMethod = '';
  private searchValue: string | string[] = '';
  private subscription = new Subscription();

  private selectedCountries: StatisticsCountryFilterItem[] = [];
  private selectedCampaigns: ISelectOption[] = [];
  private selectedAdminCampaigns: ISelectOption[] = [];

  private destroy$ = new Subject<void>();

  constructor(
    private readonly statistics: StatisticsSummaryService,
    private readonly translate: TranslateService,
    private readonly statisticsTableService: TableService,
    private readonly sharedStatisticsFunctionsService: SharedStatisticsFunctionsService
  ) {}

  ngOnInit(): void {
    this.statistics.selectedCountry
      .pipe(
        filter(countries => typeof countries !== 'boolean'),
        tap(countries => {
          this.selectedCountries = [...countries];
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.statistics.selectedCampaigns
      .pipe(
        filter(campaigns => typeof campaigns !== 'boolean'),
        tap(campaigns => {
          this.selectedCampaigns = [...campaigns];
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.statistics.selectedAdminCampaigns
      .pipe(
        filter(campaigns => typeof campaigns !== 'boolean'),
        tap(campaigns => {
          this.selectedAdminCampaigns = [...campaigns];
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.statisticsTableService
      .getSortMethod()
      .pipe(
        tap(sortMethod => (this.activeSortMethod = sortMethod)),
        takeUntil(this.destroy$)
      )
      .subscribe();

    if (Object.keys(this.appliedFiltersBar).length) {
      this.checkForActiveFilters();
    } else {
      this.enableCleanFilters = false;
    }
    if (this.activeSortMethod.sortMethod) {
      this.activeSortMethod = this.activeSortMethod.sortMethod;
    }
    this.statistics.searchInputHandler.subscribe(data => {
      if (data) {
        // @ts-ignore
        this[data.searchValueName] = data.inputValue;
        this.searchValue = data.inputValue;

        if (this.filterMenuOptions && data.menuName === this.menuName) {
          const item = this.filterMenuOptions.find(opt => opt.name === this.searchValueName);

          if (item && item.values) {
            item.values = this.searchValue;
          }
        }
      }
    });
    if (this.filterMenuOptions?.find(option => option.name === 'campaign_name_or_id')?.type) {
      // ask backend for a list of incent campaigns
      this.statistics
        .getSpecialCampaignsList(this.filterMenuOptions?.find(option => option.name === 'campaign_name_or_id')?.type)
        .pipe(take(1))
        .subscribe(res => {
          if (res.data?.campaigns) {
            const tempArray: ISelectOption[] = [];
            res.data.campaigns.forEach(item => {
              tempArray.push({ id: item.id, value: item.id, name: item.name });
            });
            this.selectOptionsCampaigns = tempArray;
          }
        });
    }
    this.prepareCountriesData();

    this.filterMenuOptions && this.setCheckboxes(this.filterMenuOptions);

    if (!isEmpty(this.statistics.currentSelectedOptions) && this.statistics.currentSelectedOptions[this.getSelectedOptionsCategory()]) {
      this.selectedOptions = { [this.getSelectedOptionsCategory()]: this.statistics.currentSelectedOptions[this.getSelectedOptionsCategory()] };
    }
  }

  ngAfterViewInit(): void {
    const columnHeight = this.columnMenu.nativeElement.offsetHeight;

    this.getOpenedColumnMenuHeight.emit(columnHeight);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public checkIfScrolled(): void {
    const parentElement = document.getElementById('countriesList')!;
    if (parentElement.scrollTop >= parentElement.scrollHeight - parentElement.clientHeight - 10) {
      this.sliceCountriesTo += 10;
    }
  }

  public selectOption(event: Event, option: Option, category: string): void {
    const checked = (event.target as HTMLInputElement).checked;

    this.selectedOptions = { [category]: [...this.selectedOptions[category].map(item => (item.id === option.id ? { ...item, checked } : item))] };

    this.checkboxError = this.selectedOptions[category].every(({ checked }) => !checked);
  }

  public selectCountry(country: StatisticsCountryFilterItem): void {
    const indexOfElement = this.selectedCountries.findIndex(opt => opt.code === country.code);
    if (indexOfElement > -1) {
      this.selectedCountries.splice(indexOfElement, 1);
    } else {
      this.selectedCountries.push(country);
    }
  }

  public selectCampaign(selectedCampaigns: unknown): void {
    this.selectedCampaigns = [...(selectedCampaigns as ISelectOption[])];
  }

  public selectAdminCampaign(selectedCampaigns: unknown): void {
    this.selectedAdminCampaigns = [...(selectedCampaigns as ISelectOption[])];
  }

  public checkIfCountryActive(countryCode: string): boolean {
    return !!this.selectedCountries.find((country: any) => country.code === countryCode);
  }

  public cleanFilters(): void {
    if (this.menuName) {
      this.statistics.cleanFilters.next(this.menuName);
      this.closeMenuHandler(this.menuName);
    }
  }

  public closeMenuHandler(name: string): void {
    this.closeMenu.emit(name);
  }

  public applyFilter(): void {
    this.statistics.setSelectedCountry(this.selectedCountries);
    this.statistics.setSelectedCampaigns(this.selectedCampaigns);
    this.statistics.setSelectedAdminCampaigns(this.selectedAdminCampaigns);
    this.statisticsTableService.setSortMethod(this.activeSortMethod);

    if (this.menuName) this.closeMenuHandler(this.menuName);

    if (this.range) {
      const rangeMinMaxVal = this.range.getSliderMinMaxValue().slice(0, 2);
      const rangeName = this.range.getSliderMinMaxValue()[2];
      this.statistics.sliderValues.next({ values: rangeMinMaxVal, name: rangeName.toUpperCase(), rangeData: this.range });
    } else {
      this.statistics.sliderValues.next({});
    }
    this.statistics.setSortMethod({ sortMethod: this.activeSortMethod, origin: '' });
    if (this.searchValue && this.menuName) {
      this.statistics.searchInputHandler.next({
        menuName: this.menuName,
        inputValue: this.searchValue as string,
        searchValueName: this.searchValueName,
        searchMethod: this.searchMethod
      });
    } else {
      this.statistics.searchInputHandler.next({
        menuName: this.menuName ?? '',
        inputValue: '',
        searchValueName: this.searchValueName,
        searchMethod: this.searchMethod
      });
    }

    if (!isEmpty(this.selectedOptions)) {
      this.statistics.setSelectedOptions.next({ [this.getSelectedOptionsCategory()]: this.selectedOptions[this.getSelectedOptionsCategory()] });
    }

    this.sharedStatisticsFunctionsService.refetch();
    this.closeMenu.emit(this.menuName);
  }

  public selectSortMethod(option: IChosenSortValue): void {
    this.activeSortMethod = option;
  }

  public searchByNameOrIDHandler(event: KeyboardEvent, filterOption: FilterMenuOption): void {
    this.searchValueName = filterOption.name ?? '';
    this.searchMethod = filterOption.searchMethod ?? '';
    this.searchValue = (event.target as HTMLInputElement).value.trim();
  }

  public getModel(filterName: string): string {
    return this.filterMenuOptions?.find(opt => opt.name === filterName)?.values;
  }

  private prepareCountriesData(): void {
    const tempList: StatisticsCountryFilterItem[] = [];
    const countriesKeys = Object.keys(countries);
    countriesKeys.forEach((item, index) => {
      tempList.push({
        countryNumber: countriesKeys.indexOf(item) + 1,
        id: item,
        code: item,
        translate: this.translate.instant('countries.' + index).name,
        fieldType: 'select'
      });
    });
    this.countryList = tempList.sort((a, b) => (a.translate > b.translate ? 1 : b.translate > a.translate ? -1 : 0));
  }

  private checkForActiveFilters(): void {
    if (!this.filterMenuOptions) return;

    const currentFilterBarOptions = Object.keys(this.appliedFiltersBar);
    const checkboxesList = this.filterMenuOptions.find(filter => filter.category === 'checkboxesList');
    if (checkboxesList) {
      this.enableCleanFilters = currentFilterBarOptions.includes(Object.keys(checkboxesList.values)[0]);
    }
    const slider = this.filterMenuOptions.find(filter => filter.category === 'slider');
    if (slider) {
      this.enableCleanFilters = currentFilterBarOptions.includes(slider.name ?? '');
    }
    const countriesSelect = this.filterMenuOptions.find(filter => filter.category === 'countriesSelect');
    if (countriesSelect) {
      this.enableCleanFilters = currentFilterBarOptions.includes(countriesSelect.name ?? '');
    }
  }

  private getSelectedOptionsCategory = () => {
    return Object.keys(this.selectedOptions)[0];
  };

  private setCheckboxes = (filters: FilterMenuOption[]) => {
    const values = filters.find(item => item.category === 'checkboxesList')?.values as Record<string, Option[]>;

    if (isEmpty(values)) return;

    const category = Object.keys(values)[0];

    this.selectedOptions = { [category]: [...values[category]] };
  };
}
